import { useCallback, useState } from 'react'

import { sfx } from '../../../lib/sound'
import type { GameProps } from '../GameShell'

const WIDTH = 9
const HEIGHT = 9
const MINES = 10

interface Cell {
  mine: boolean
  adjacent: number
  revealed: boolean
  flagged: boolean
}

function neighbours(index: number): number[] {
  const x = index % WIDTH
  const y = Math.floor(index / WIDTH)
  const result: number[] = []
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue
      const nx = x + dx
      const ny = y + dy
      if (nx >= 0 && nx < WIDTH && ny >= 0 && ny < HEIGHT) {
        result.push(ny * WIDTH + nx)
      }
    }
  }
  return result
}

/**
 * Lay mines, avoiding `safe` and its neighbours.
 *
 * The board is built on the *first click* rather than up front, so the opening
 * move can never be a mine -- losing on move one is not a game, it is a coin
 * toss.
 */
function buildBoard(safe: number): Cell[] {
  const forbidden = new Set([safe, ...neighbours(safe)])
  const positions = Array.from({ length: WIDTH * HEIGHT }, (_, i) => i).filter(
    (i) => !forbidden.has(i),
  )
  for (let i = positions.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[positions[i], positions[j]] = [positions[j], positions[i]]
  }
  const mines = new Set(positions.slice(0, MINES))

  return Array.from({ length: WIDTH * HEIGHT }, (_, i) => ({
    mine: mines.has(i),
    adjacent: neighbours(i).filter((n) => mines.has(n)).length,
    revealed: false,
    flagged: false,
  }))
}

const NUMBER_COLORS = [
  '',
  'text-brand',
  'text-positive',
  'text-negative',
  'text-brand',
  'text-negative',
  'text-positive',
  'text-ink',
  'text-muted',
]

export default function Minesweeper({ onGameOver, onScore }: GameProps) {
  const [board, setBoard] = useState<Cell[] | null>(null)
  const [dead, setDead] = useState(false)
  const [won, setWon] = useState(false)

  const revealed = board?.filter((c) => c.revealed).length ?? 0
  const flags = board?.filter((c) => c.flagged).length ?? 0

  const open = useCallback(
    (index: number) => {
      if (dead || won) return

      let cells = board
      if (!cells) {
        cells = buildBoard(index)
      }
      if (cells[index].flagged || cells[index].revealed) return

      const next = cells.map((c) => ({ ...c }))

      if (next[index].mine) {
        next.forEach((c) => {
          if (c.mine) c.revealed = true
        })
        setBoard(next)
        setDead(true)
        sfx.thud()
        onGameOver(revealed)
        return
      }

      // Flood-fill outward from any cell with no adjacent mines. Iterative
      // rather than recursive: an empty board is 71 cells deep and would risk
      // blowing the stack on a bad day.
      const stack = [index]
      while (stack.length > 0) {
        const at = stack.pop()!
        const cell = next[at]
        if (cell.revealed || cell.flagged) continue
        cell.revealed = true
        if (cell.adjacent === 0) {
          stack.push(...neighbours(at))
        }
      }

      const safeCells = next.filter((c) => !c.mine).length
      const openedCells = next.filter((c) => c.revealed).length
      setBoard(next)
      onScore?.(openedCells)
      sfx.tick()

      if (openedCells === safeCells) {
        setWon(true)
        sfx.complete()
        // Clearing the board is worth more than merely surviving.
        onGameOver(openedCells + MINES * 5)
      }
    },
    [board, dead, won, onGameOver, onScore, revealed],
  )

  function flag(event: React.MouseEvent, index: number) {
    event.preventDefault()
    if (!board || dead || won || board[index].revealed) return
    const next = board.map((c) => ({ ...c }))
    next[index].flagged = !next[index].flagged
    setBoard(next)
  }

  const cells =
    board ??
    Array.from({ length: WIDTH * HEIGHT }, () => ({
      mine: false,
      adjacent: 0,
      revealed: false,
      flagged: false,
    }))

  return (
    <div className="space-y-3">
      <p className="text-center text-sm text-muted">
        {MINES - flags} mines left · right-click or long-press to flag
      </p>
      <div
        className="mx-auto grid w-full max-w-md gap-px rounded-xl bg-border-subtle p-px"
        style={{ gridTemplateColumns: `repeat(${WIDTH}, minmax(0, 1fr))` }}
      >
        {cells.map((cell, index) => (
          <button
            key={index}
            onClick={() => open(index)}
            onContextMenu={(e) => flag(e, index)}
            className={`grid aspect-square place-items-center text-sm font-semibold ${
              cell.revealed
                ? cell.mine
                  ? 'bg-negative/20'
                  : 'bg-canvas'
                : 'bg-surface hover:bg-brand-soft'
            }`}
          >
            {cell.revealed ? (
              cell.mine ? (
                '💣'
              ) : cell.adjacent > 0 ? (
                <span className={NUMBER_COLORS[cell.adjacent]}>{cell.adjacent}</span>
              ) : (
                ''
              )
            ) : cell.flagged ? (
              '🚩'
            ) : (
              ''
            )}
          </button>
        ))}
      </div>
    </div>
  )
}
